import { ChangeEvent, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_SEARCH_LENGTH = 2;

export type UseSearchOptions = {
  /** Debounce delay in ms before searchReady updates. Default 300. */
  delay?: number;
  /** Minimum length (after trim) before search is considered "ready". Default 2. */
  minSearchLength?: number;
  /** Called when searchReady changes (after delay, trimmed, and length >= minSearchLength; otherwise ""). */
  onSearchReadyChange?: (searchReady: string) => void;
  /** Initial search value. */
  searchValue?: string;
};

export const useSearch = (options: UseSearchOptions) => {
  const {
    delay = DEFAULT_DEBOUNCE_MS,
    minSearchLength = DEFAULT_MIN_SEARCH_LENGTH,
    searchValue = '',
    onSearchReadyChange,
  } = options;
  const [inputSearch, setInputSearch] = useState(searchValue);

  const [debouncedInput] = useDebounce(inputSearch, delay);
  const searchReady = debouncedInput.trim().length >= minSearchLength ? debouncedInput.trim() : '';

  useEffect(() => {
    setInputSearch(searchValue);
  }, [searchValue]);

  useEffect(() => {
    onSearchReadyChange?.(searchReady);
  }, [searchReady]);

  const onInputSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputSearch(e.target.value);
  };
  const clearSearch = () => {
    setInputSearch('');
  };
  return {
    inputSearch,
    /** Delayed, trimmed, and only non-empty when length >= minSearchLength. Use for query/API. */
    searchReady,
    onInputSearchChange,
    clearSearch,
  };
};
